import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Form, Input, Switch, Select, Button, Space, Typography, Table,
  Divider, Alert, Tag, Row, Col, message as antMessage,
} from 'antd';
import {
  ApiOutlined, SaveOutlined, ThunderboltOutlined, PlusOutlined,
  DeleteOutlined, CloudUploadOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import apiService from '../services/apiService';

const { Text, Title, Paragraph } = Typography;

// Common QQCRM Lead fields available as mapping sources.
const LEAD_FIELDS = [
  'fullName', 'firstName', 'lastName', 'email', 'phone', 'company',
  'jobTitle', 'address', 'website', 'source', 'status', 'presalesNotes',
];

const methodColor = {
  GET: 'blue', POST: 'green', PUT: 'orange', PATCH: 'gold', DELETE: 'red',
};

const DpdpaIntegrationSettings = () => {
  const [form] = Form.useForm();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [apis, setApis] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/dpdpa/config');
      const data = res.data.data;
      setConfig(data);
      form.setFieldsValue({
        enabled: data.enabled,
        baseUrl: data.baseUrl,
        authToken: '',
        authHeader: data.authHeader,
        authScheme: data.authScheme,
        updateMethod: data.updateMethod,
        externalIdPath: data.externalIdPath,
        endpoints: data.endpoints,
        fieldMap: data.fieldMap?.length ? data.fieldMap : [{ source: '', target: '' }],
      });
    } catch {
      antMessage.error('Failed to load DPDPA settings');
    } finally {
      setLoading(false);
    }
  }, [form]);

  const fetchApis = useCallback(async () => {
    try {
      const res = await apiService.get('/dpdpa/apis');
      setApis(res.data.data);
    } catch {
      /* non-critical */
    }
  }, []);

  useEffect(() => { fetchConfig(); fetchApis(); }, [fetchConfig, fetchApis]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = { ...values };
      // Drop the token field when left blank so the existing key is kept.
      if (!payload.authToken) delete payload.authToken;
      payload.fieldMap = (values.fieldMap || []).filter((m) => m && m.source && m.target);
      const res = await apiService.put('/dpdpa/config', payload);
      setConfig(res.data.data);
      form.setFieldValue('authToken', '');
      antMessage.success('DPDPA settings saved');
    } catch (err) {
      antMessage.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      // Persist current form first so the test uses the latest base URL.
      await form.validateFields();
      await handleSave(form.getFieldsValue());
      const res = await apiService.post('/dpdpa/test');
      if (res.data.success) antMessage.success(res.data.message || 'Connection OK');
      else antMessage.error(res.data.message || 'Connection failed');
    } catch (err) {
      antMessage.error(err.response?.data?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const renderApiTable = (group) => (
    <Table
      size="small"
      pagination={false}
      rowKey={(r) => `${r.method}-${r.path}`}
      dataSource={group?.endpoints || []}
      columns={[
        {
          title: 'Method', dataIndex: 'method', width: 90,
          render: (m) => <Tag color={methodColor[m] || 'default'}>{m}</Tag>,
        },
        {
          title: 'Path', dataIndex: 'path',
          render: (p) => <Text code>{p}</Text>,
        },
        { title: 'Description', dataIndex: 'description' },
      ]}
    />
  );

  return (
    <div>
      <div className="mb-4">
        <Title level={5}>
          <ApiOutlined className="mr-2" />
          DPDPA App Integration
        </Title>
        <Text type="secondary">
          Connect this CRM to your DPDPA application. Configure the connection and field
          mapping below to push, edit, or delete leads in the DPDPA app.
        </Text>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSave} disabled={loading}>
        <Card title="Connection" className="mb-4">
          <div className="flex justify-between items-center mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div>
              <Text strong className="block">Enable DPDPA integration</Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Outbound push/edit/delete calls are blocked until this is on.
              </Text>
            </div>
            <Form.Item name="enabled" valuePropName="checked" className="mb-0">
              <Switch />
            </Form.Item>
          </div>

          <Form.Item
            name="baseUrl"
            label="DPDPA App Base URL"
            rules={[{ required: true, message: 'Base URL is required' }, { type: 'url', message: 'Enter a valid URL' }]}
          >
            <Input placeholder="https://dpdpa.example.com/api" />
          </Form.Item>

          <Form.Item
            name="authToken"
            label="Auth Token / API Key"
            tooltip={config?.hasAuthToken ? 'Leave blank to keep the existing token' : undefined}
          >
            <Input.Password
              placeholder={config?.hasAuthToken ? '•••• token on file — leave blank to keep' : 'Token used to authenticate against the DPDPA app'}
              autoComplete="new-password"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="authHeader" label="Auth Header Name">
                <Input placeholder="Authorization" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="authScheme" label="Auth Scheme Prefix" tooltip="e.g. 'Bearer'. Leave blank to send the raw token.">
                <Input placeholder="Bearer" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Endpoints & Payload" className="mb-4">
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name={['endpoints', 'push']} label="Create (POST) path">
                <Input placeholder="/leads" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={['endpoints', 'update']} label="Edit path (:id)">
                <Input placeholder="/leads/:id" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name={['endpoints', 'delete']} label="Delete path (:id)">
                <Input placeholder="/leads/:id" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="updateMethod" label="Edit HTTP Method">
                <Select
                  options={['PUT', 'PATCH', 'POST'].map((v) => ({ label: v, value: v }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="externalIdPath"
                label="External ID path in response"
                tooltip="Dot-path to the created record's id in the DPDPA response, e.g. 'id' or 'data.id'."
              >
                <Input placeholder="id" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Field Mapping"
          className="mb-4"
          extra={<Text type="secondary" style={{ fontSize: 12 }}>QQCRM Lead field → DPDPA payload field</Text>}
        >
          <Form.List name="fieldMap">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...rest}
                      name={[name, 'source']}
                      rules={[{ required: true, message: 'Source field' }]}
                      style={{ marginBottom: 0, minWidth: 220 }}
                    >
                      <Select
                        showSearch
                        placeholder="QQCRM Lead field"
                        options={LEAD_FIELDS.map((f) => ({ label: f, value: f }))}
                      />
                    </Form.Item>
                    <span style={{ color: '#999' }}>→</span>
                    <Form.Item
                      {...rest}
                      name={[name, 'target']}
                      rules={[{ required: true, message: 'Target field' }]}
                      style={{ marginBottom: 0, minWidth: 220 }}
                    >
                      <Input placeholder="DPDPA payload field" />
                    </Form.Item>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ source: '', target: '' })} icon={<PlusOutlined />} block>
                  Add mapping
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        {config?.stats && (
          <div className="mb-4">
            <Space size="middle" wrap>
              <Tag color="green">Pushed: {config.stats.pushed || 0}</Tag>
              <Tag color="orange">Updated: {config.stats.updated || 0}</Tag>
              <Tag color="red">Deleted: {config.stats.deleted || 0}</Tag>
              <Tag>Errors: {config.stats.errors || 0}</Tag>
              {config.lastResult?.at && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Last: {config.lastResult.success ? '✓' : '✗'} {config.lastResult.message}
                </Text>
              )}
            </Space>
          </div>
        )}

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
              Save Settings
            </Button>
            <Button icon={<ThunderboltOutlined />} loading={testing} onClick={handleTest}>
              Test Connection
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Alert
        className="mb-4"
        type="info"
        showIcon
        icon={<CloudUploadOutlined />}
        message="Pushing leads"
        description={
          <span>
            Push, edit, and delete actions run per-lead from the Leads page (or via the
            API below). The first push stores the DPDPA record id on the lead, so later
            edits and deletes target the same record.
          </span>
        }
      />

      <Divider />

      <Title level={5}>
        <InfoCircleOutlined className="mr-2" />
        APIs that help the DPDPA app
      </Title>

      {apis?.inbound && (
        <Card size="small" className="mb-4" title="Inbound — DPDPA app → QQCRM">
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>{apis.inbound.description}</Paragraph>
          {renderApiTable(apis.inbound)}
        </Card>
      )}

      {apis?.outbound && (
        <Card size="small" title="Outbound — QQCRM → DPDPA app">
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>{apis.outbound.description}</Paragraph>
          {renderApiTable(apis.outbound)}
        </Card>
      )}
    </div>
  );
};

export default DpdpaIntegrationSettings;
